// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useState } from 'react';

import type { TaskId } from '@evolvemep/api-ts';
import { Card, List, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { CollapseWithLoading } from 'components/Mantine/CollapseWithLoading';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries, strongKeys } from 'helpers/strongEntries';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { CancelWorkOrderTasks } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/CancellationAndBlocking/CancelWorkOrderTasks';
import { useStartedTasks } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/CancellationAndBlocking/useStartedTasks';
import type { WorkOrderActionModalProps } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/workOrderActions';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

type Props = WorkOrderActionModalProps & {
  cancellationRequest?: boolean;
};

export const BlockWorkOrderModal = ({ workOrder, onWorkOrderActioned, cancellationRequest, ...props }: Props) => {
  const { acceptCancelRequest, blockWorkOrder } = useWorkOrders();
  const [updating, setUpdating] = useState(false);

  const [tasksToUpdate, setTasksToUpdate] = useState<Record<TaskId, boolean>>({});
  const [readyToSubmit, setReadyToSubmit] = useState(false);

  const setTaskDecision = useCallback((taskId: TaskId, isSaveData: boolean) => {
    setTasksToUpdate((tasks) => ({
      ...tasks,
      [taskId]: isSaveData,
    }));
  }, []);

  const { loading: loadingTasks, tasks } = useStartedTasks(workOrder);

  useEffect(() => {
    const taskIds = strongKeys(tasksToUpdate);
    setReadyToSubmit(taskIds.length === tasks?.length);
  }, [tasks?.length, tasksToUpdate]);

  const hasInProgressTasks = isNotNil(tasks) && tasks.length > 0;

  const onSubmit = async () => {
    setUpdating(true);
    const taskActions = strongEntries(tasksToUpdate).map(([taskId, saveProgress]) => ({
      taskId,
      saveProgress,
    }));
    await (cancellationRequest ? acceptCancelRequest : blockWorkOrder)(workOrder.workOrderId, taskActions)
      .then(onWorkOrderActioned)
      .then(() => {
        notifications.show({
          color: 'green',
          title: 'Successfully updated',
          message: `Blocked work order ${getDisplayNameWO(workOrder)}`,
        });
      })
      .finally(() => setUpdating(false));
  };

  return (
    <WrappedModal
      title="Block work order"
      onConfirm={onSubmit}
      disabled={!readyToSubmit || loadingTasks}
      size={hasInProgressTasks ? 'xl' : 'md'}
      confirmationText="Block"
      buttonColor="red"
      {...props}
    >
      <Text fz="sm">
        Blocking this work order will allow you to make changes and release it again when ready.{' '}
        {hasInProgressTasks && (
          <>
            You can also decide how to handle the <b>in progress tasks</b> listed below.
          </>
        )}
      </Text>
      <CollapseWithLoading loading={loadingTasks}>
        <Card bg="app-shell-gray.0">
          <Card.Section p="sm">
            <Text fz="sm" mb={3}>
              This will:
            </Text>
            <List fz="sm">
              <List.Item>
                Change the work order status to <b>Blocked</b>
              </List.Item>
              {hasInProgressTasks && <List.Item>Pause in progress tasks</List.Item>}
              <List.Item>Remove all tasks from their work cells</List.Item>
            </List>
          </Card.Section>
        </Card>
      </CollapseWithLoading>
      {hasInProgressTasks && (
        <CancelWorkOrderTasks
          workOrder={workOrder}
          tasks={tasks}
          tasksToUpdate={tasksToUpdate}
          setTaskDecision={setTaskDecision}
          disabled={updating}
        />
      )}
    </WrappedModal>
  );
};
